import { PRO_PRICE_PER_MONTH, PRO_PRICE_TEXT } from "~/utils/pricing";
import { useState } from "react";
import { StyleSheet, View } from "react-native";

import {
  AppText,
  Button,
  ButtonText,
  Card,
  ProtectedScreen,
  SegmentedControl,
  Stack,
  TextField,
} from "~/components/ui";
import { useAuth } from "~/hooks/useAuth";
import { usePrimaryWorkspace, useWorkspaceTeam } from "~/hooks/workspace";
import {
  radii,
  spacing,
  useThemedStyles,
  type ThemeColors,
} from "~/styles/theme";

type InviteRole = "admin" | "member" | "viewer";

const roleOptions: { label: string; value: InviteRole }[] = [
  { label: "Admin", value: "admin" },
  { label: "Member", value: "member" },
  { label: "Accountant (free)", value: "viewer" },
];

function roleLabel(role: string) {
  if (role === "owner") return "Owner";
  if (role === "admin") return "Admin";
  if (role === "viewer") return "Accountant · free";
  return "Member";
}

export default function TeamScreen() {
  const styles = useThemedStyles(makeStyles);
  const { user } = useAuth();
  const workspace = usePrimaryWorkspace();
  const team = useWorkspaceTeam(workspace.data?.id);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<InviteRole>("member");
  const members = team.members;
  const invitations = team.invitations;
  const canManage = Boolean(
    workspace.data?.owner_id === user?.id ||
    members.some(
      (member) => member.user_id === user?.id && member.role === "admin",
    ),
  );
  const workingSeats =
    1 +
    members.filter(
      (member) =>
        member.user_id !== workspace.data?.owner_id && member.role !== "viewer",
    ).length;
  const acceptedEmail = new Map(
    invitations
      .filter(
        (invitation) =>
          invitation.status === "accepted" && invitation.accepted_by_id,
      )
      .map((invitation) => [invitation.accepted_by_id, invitation.email]),
  );
  const error =
    team.error ??
    team.invite.error ??
    team.resend.error ??
    team.revoke.error ??
    team.updateMemberRole.error ??
    team.removeMember.error;

  async function invite() {
    if (!email.trim() || !canManage) return;
    await team.invite.mutateAsync({ email, role });
    setEmail("");
    setRole("member");
  }

  return (
    <ProtectedScreen
      title="Seats & accountant access"
      subtitle={`MintStint Pro is ${PRO_PRICE_PER_MONTH} flat. Invite teammates included. Accountant seats are free and view-only.`}
    >
      <View style={styles.metrics}>
        <View style={styles.metric}>
          <Card gap="xs">
            <AppText size="xs">Working seats</AppText>
            <AppText size="lg" weight="bold">
              {workingSeats}
            </AppText>
          </Card>
        </View>
        <View style={styles.metric}>
          <Card gap="xs">
            <AppText size="xs">Plan price</AppText>
            <AppText size="lg" weight="bold">
              {PRO_PRICE_TEXT}
            </AppText>
          </Card>
        </View>
        <View style={styles.metric}>
          <Card gap="xs">
            <AppText size="xs">Free accountant seats</AppText>
            <AppText size="lg" weight="bold">
              {members.filter((member) => member.role === "viewer").length}
            </AppText>
          </Card>
        </View>
      </View>

      {canManage ? (
        <Card gap="md">
          <Stack gap="xs">
            <AppText weight="semibold">Invite person</AppText>
            <AppText size="xs">
              Accountants can view clients, invoices, expenses, receipts,
              reports, and exports. They cannot change data.
            </AppText>
          </Stack>
          <TextField
            accessibilityLabel="Invitation email"
            value={email}
            placeholder="accountant@example.co.nz"
            keyboardType="email-address"
            onChangeText={setEmail}
          />
          <SegmentedControl
            accessibilityLabel="Invitation role"
            options={roleOptions}
            value={role}
            onChange={setRole}
          />
          {error ? (
            <AppText tone="danger" size="xs">
              {error instanceof Error
                ? error.message
                : "Unable to update seats."}
            </AppText>
          ) : null}
          <Button
            align="start"
            disabled={!email.trim() || team.invite.isPending}
            onPress={() => void invite()}
          >
            <ButtonText>
              {team.invite.isPending ? "Sending..." : "Send invitation"}
            </ButtonText>
          </Button>
        </Card>
      ) : null}

      <Card gap="md">
        <AppText weight="semibold">Workspace access</AppText>
        {members.map((member) => {
          const isOwner = member.user_id === workspace.data?.owner_id;
          const isYou = member.user_id === user?.id;
          const editable = canManage && !isOwner && !isYou;
          return (
            <View key={member.id} style={styles.row}>
              <View style={styles.rowMain}>
                <AppText weight="semibold">
                  {isYou
                    ? user?.email
                    : (acceptedEmail.get(member.user_id) ??
                      `Member ${member.user_id.slice(0, 8)}`)}
                </AppText>
                <AppText size="xs">
                  {isOwner ? "Owner" : roleLabel(member.role)}
                </AppText>
              </View>
              {editable ? (
                <View style={styles.actions}>
                  <SegmentedControl
                    accessibilityLabel="Member role"
                    options={roleOptions}
                    value={member.role as InviteRole}
                    onChange={(nextRole) =>
                      void team.updateMemberRole.mutateAsync({
                        memberId: member.id,
                        role: nextRole,
                      })
                    }
                  />
                  <Button
                    compact
                    variant="outline"
                    disabled={team.removeMember.isPending}
                    onPress={() =>
                      void team.removeMember.mutateAsync(member.id)
                    }
                  >
                    <ButtonText>Remove</ButtonText>
                  </Button>
                </View>
              ) : null}
            </View>
          );
        })}
      </Card>

      <Card gap="md">
        <AppText weight="semibold">Invitations</AppText>
        {invitations.length === 0 ? (
          <AppText size="xs">No invitations yet.</AppText>
        ) : null}
        {invitations.map((invitation) => (
          <View key={invitation.id} style={styles.row}>
            <View style={styles.rowMain}>
              <AppText weight="semibold">{invitation.email}</AppText>
              <AppText size="xs">
                {roleLabel(invitation.role)} · {invitation.status}
              </AppText>
              {invitation.delivery_error ? (
                <AppText tone="danger" size="xs">
                  {invitation.delivery_error}
                </AppText>
              ) : null}
            </View>
            {canManage && invitation.status === "pending" ? (
              <View style={styles.actions}>
                <Button
                  compact
                  variant="outline"
                  onPress={() => void team.resend.mutateAsync(invitation.id)}
                >
                  <ButtonText>Resend</ButtonText>
                </Button>
                <Button
                  compact
                  variant="outline"
                  onPress={() => void team.revoke.mutateAsync(invitation.id)}
                >
                  <ButtonText>Revoke</ButtonText>
                </Button>
              </View>
            ) : null}
          </View>
        ))}
      </Card>
    </ProtectedScreen>
  );
}

const makeStyles = (colors: ThemeColors) =>
  StyleSheet.create({
    metrics: { flexDirection: "row", flexWrap: "wrap", gap: spacing.m },
    metric: { minWidth: 150, flex: 1 },
    row: {
      flexDirection: "row",
      flexWrap: "wrap",
      alignItems: "center",
      gap: spacing.m,
      padding: spacing.m,
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: radii.m,
    },
    rowMain: { minWidth: 220, flex: 1, gap: spacing.xs },
    actions: {
      flexDirection: "row",
      flexWrap: "wrap",
      alignItems: "center",
      gap: spacing.s,
    },
  });
